// Datumhulp voor de Planner To-Do (inplannen, deadlines, werkdagen).
// Verhuisd uit PlannerTodo.jsx; gedrag ongewijzigd.
import { werkdagenVoor } from './rooster';

export { werkdagenVoor };

const DAY_NAMES = ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'];
const MONTH_NAMES = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

export function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

export function addWorkDays(date, days) {
  const d = new Date(date);
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) added++;
  }
  return d;
}

export function subWorkDays(date, days) {
  const d = new Date(date);
  let removed = 0;
  while (removed < days) {
    d.setDate(d.getDate() - 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) removed++;
  }
  return d;
}

export function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Weekenddagen zijn geen werkdagen — schuif door naar de eerstvolgende maandag. */
export function nextWorkDay(date) {
  const d = startOfDay(date);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  return d;
}

export function eindDatumVoor(start, dagen) {
  return dagen > 1 ? addWorkDays(start, dagen - 1) : new Date(start);
}

export function formatISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** 'YYYY-MM-DD' (of met tijd erachter) als lokale datum — `new Date('2026-12-07')` is UTC. */
export function lokaleDatum(iso) {
  const [y, m, d] = String(iso || '').slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export function daysUntil(dateStr) {
  const target = lokaleDatum(dateStr);
  if (!target) return Infinity;
  const today = startOfDay(new Date());
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
}

/**
 * Plant een taak in vanaf de gewenste startdag, maar respecteert de deadline:
 * loopt de taak eroverheen dan schuift de start naar voren tot het einde precies
 * op de deadline valt. Kan dat niet meer (deadline te dichtbij), dan blijft de
 * gewenste start staan en geeft `waarschuwing` aan wat er misgaat.
 */
export function planTaak(task, gewensteStart) {
  const dagen = werkdagenVoor(task);
  const vandaag = nextWorkDay(new Date());
  let start = nextWorkDay(Math.max(startOfDay(gewensteStart).getTime(), vandaag.getTime()));
  let eind = eindDatumVoor(start, dagen);
  let waarschuwing = null;
  let verschoven = false;

  const deadline = task.deadline ? lokaleDatum(task.deadline) : null;
  if (deadline && !isNaN(deadline) && eind > deadline) {
    const laatsteStart = nextWorkDay(subWorkDays(deadline, dagen - 1));
    if (eindDatumVoor(laatsteStart, dagen) <= deadline && laatsteStart >= vandaag) {
      start = laatsteStart;
      eind = eindDatumVoor(start, dagen);
      verschoven = true;
    } else {
      waarschuwing = `Past niet meer vóór de deadline (${formatISO(deadline)}) — ${dagen} werkdag(en) nodig.`;
    }
  }

  return { start, eind, waarschuwing, verschoven };
}

export function generateWorkingDays(startDate, numWeeks = 5) {
  const days = [];
  let current = new Date(startDate);
  const endDate = addDays(startDate, numWeeks * 7);
  // Exclusieve grens: met <= viel de maandag van de vólgende week ook in het venster.
  while (current < endDate) {
    const dayOfWeek = current.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      days.push({
        date: new Date(current),
        iso: formatISO(current),
        dayName: DAY_NAMES[dayOfWeek],
        dayNum: current.getDate(),
        month: MONTH_NAMES[current.getMonth()],
        isFirstOfMonth: current.getDate() === 1 || days.length === 0,
      });
    }
    current = addDays(current, 1);
  }
  return days;
}

/** Agenda-uren van een persoon; e-mail hoofdletterongevoelig (Leon@ vs leon@). */
export function agendaVan(calendarHours, person, gedeeld) {
  const email = String(person?.email || '').trim().toLowerCase();
  if (!email || gedeeld?.has(email)) return {};
  return calendarHours?.[email] || {};
}

/** Aantal werkdagen van `vanISO` naar `naarISO` (negatief = terug). Weekenddagen tellen niet. */
export function werkdagVerschil(vanISO, naarISO) {
  const a = nextWorkDay(lokaleDatum(vanISO));
  const b = nextWorkDay(lokaleDatum(naarISO));
  if (!a || !b) return 0;
  const richting = b >= a ? 1 : -1;
  let n = 0;
  const d = new Date(a);
  while ((richting > 0 && d < b) || (richting < 0 && d > b)) {
    d.setDate(d.getDate() + richting);
    if (d.getDay() !== 0 && d.getDay() !== 6) n += richting;
  }
  return n;
}

/**
 * Schuif een datum `n` werkdagen op. `n = 0` laat de datum ongemoeid (ook een weekenddag:
 * alleen de persoon wijzigen mag de einddatum niet van zo 27 naar ma 28 zetten). Bij
 * n ≠ 0 tellen alleen werkdagen waar je op landt: zo +1 = ma, za −1 = vr.
 */
export function schuifWerkdagen(iso, n) {
  const d = lokaleDatum(iso);
  if (!d) return String(iso || '').slice(0, 10);
  if (!n) return formatISO(d);
  return formatISO(n > 0 ? addWorkDays(d, n) : subWorkDays(d, -n));
}

/** Aantal werkdagen van start t/m eind (inclusief); weekenden tellen niet. Geen bovengrens. */
export function looptijdWerkdagen(startISO, eindISO) {
  const s = lokaleDatum(startISO);
  const e = lokaleDatum(eindISO || startISO);
  if (!s || !e || e < s) return 0;
  let n = 0;
  for (let d = new Date(s); d <= e; d = addDays(d, 1)) if (d.getDay() !== 0 && d.getDay() !== 6) n += 1;
  return n;
}

/**
 * Eén periode `n` werkdagen verschuiven — dezelfde regel voor een losse taak en voor
 * elke taak in een projectblok. `n = 0` (alleen van persoon wisselen) laat start én
 * eind exact staan: een taak vr 25 t/m zo 27 blijft vr 25 t/m zo 27.
 */
export function schuifPeriode(startISO, eindISO, n) {
  const s = String(startISO || '').slice(0, 10);
  const e = String(eindISO || startISO || '').slice(0, 10);
  if (!n) return { start: s, eind: e };
  return { start: schuifWerkdagen(s, n), eind: schuifWerkdagen(e, n) };
}

/**
 * Een bestaande taak naar `doelISO` slepen: de start wordt precies de gekozen dag,
 * het eind schuift evenveel werkdagen mee (looptijd blijft). Zelfde dag = eind
 * ongewijzigd; het eind komt nooit vóór de start.
 */
export function verschuifNaar(startISO, eindISO, doelISO) {
  const s = String(startISO || '').slice(0, 10);
  const doel = String(doelISO || '').slice(0, 10);
  const n = s === doel ? 0 : werkdagVerschil(s, doel);
  const { eind } = schuifPeriode(s, eindISO, n);
  return { start: doel, eind: eind < doel ? doel : eind, n };
}
